import { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import _ from "lodash";

import { getSystemOptionVariants, getSystemOptionVariantsCount, getSystemUnitFromTotal, ISystemVariant } from "src/rules/calculation/systemHelpers";
import { getBetMaxStake, getBetMinStake } from "src/common/helpers/restrictions";
import NotifyMessageItem from "src/components/notifyMessages/messageItem";
import { BetCalculationsEnum, getBetCalculations } from "calculation";
import { NotifyTypeEnum } from "src/constants/enums/notifyTypes";
import { getIsScrolledToBottom } from "src/common/helpers/view";
import NumericInput from "src/common/components/numericInput";
import { useTranslation } from "src/hooks/translation";
import selectionsStore from "src/stores/selections";
import { IDefer } from "src/constants/interfaces";
import upperDataStore from "src/stores/upperData";
import { stakeOutOfLimits } from "src/rules/bet";
import Money from "src/common/components/money";
import { useConfig } from "src/hooks/config";
import CalculatorVariant from "./variant";
import betBuilderStore from "src/stores/betBuilder";
import { getEffectiveBankerCount, getEffectiveSelectionsForCalc } from "src/common/helpers/betBuilder";
import mainStore from "src/stores/main";

import './style.scss'

interface IProps {
  defer: IDefer<boolean>;
};

const CalculatorPopup = ({ defer }: IProps) => {
  const t = useTranslation();
  const config = useConfig();
  const [mainState, setMainState] = useRecoilState(mainStore)
  const { betType, freeBets, stake: initialStake, systemCombinationCount } = mainState;

  const upperDataState = useRecoilValue(upperDataStore);
  const { partnerConfig, currencyConfig } = upperDataState;

  const { selections: initialSelections } = useRecoilValue(selectionsStore);
  const { groups: betBuilderGroups } = useRecoilValue(betBuilderStore);
  const selections = useRef(initialSelections);
  const [allVariants, setAllVariants] = useState<ISystemVariant[]>([]);
  const [showCount, setShowCount] = useState(40);

  const currentVariants = useMemo(() => allVariants.slice(0, showCount), [allVariants, showCount]);

  // Collapse BB cards into single virtual legs (with their combined price)
  // before running the system combination math — otherwise 1 BB card with 2
  // legs would be treated as 2 separate legs.
  const collapsedSelections = useMemo(
    () => getEffectiveSelectionsForCalc(selections.current, betBuilderGroups) || selections.current,
    [selections, betBuilderGroups],
  );

  const [bankersCount, bankersTotalPrice] = useMemo(() => {
    const bankers = collapsedSelections.filter(item => item.isBanker);
    return [
      getEffectiveBankerCount(selections.current),
      bankers.reduce((acc, item) => acc * item.event.price, 1),
    ];
  }, [collapsedSelections, selections]);

  const optionVariantsCount = getSystemOptionVariantsCount(
    systemCombinationCount - bankersCount,
    collapsedSelections.length - bankersCount,
  );

  const [totalStake, setTotalStake] = useState(initialStake);

  const unitStake = totalStake ? getSystemUnitFromTotal(totalStake, optionVariantsCount, currencyConfig) : '0';

  const selectedFreeBet = freeBets.find(item => item.selected);

  const { possibleWin } = useMemo(() => (
    +totalStake ? (
      getBetCalculations([BetCalculationsEnum.PossibleWin], {
        config,
        partnerConfig,
        betType,
        stake: +totalStake,
        events: selections.current,
        systemCombinationCount,
        selectedFreeBet,
        currencyConfig,
      })
    ) : { possibleWin: 0 }
  ), [config, partnerConfig, currencyConfig, totalStake, betType, selections, systemCombinationCount, selectedFreeBet]);

  useEffect(() => {
    // switch to useTransition React v18.
    // Use collapsed selections so each BB card is one variant leg.
    const variants = getSystemOptionVariants(collapsedSelections, systemCombinationCount - bankersCount);
    setTimeout(() => setAllVariants(variants), 0);
  }, [collapsedSelections, systemCombinationCount, bankersCount]);

  const onScroll = (e: SyntheticEvent<HTMLElement>) => {
    const isScrolledToBottom = getIsScrolledToBottom(e.currentTarget);
    if (isScrolledToBottom && showCount < allVariants.length) setShowCount(showCount + 40);
  }

  const onSave = () => {
    if (totalStake !== initialStake) {
      setMainState(oldMainState => ({
        ...oldMainState,
        stake: totalStake,
        systemUnitStake: getSystemUnitFromTotal(totalStake, optionVariantsCount, currencyConfig),
      }));
    }

    defer.resolve(true);
  }

  const stakeErrorMessage = useMemo(() => {
    const optimisticMainStateWithNewStake = { ...mainState, stake: totalStake };

    if (!stakeOutOfLimits(config, selections.current, optimisticMainStateWithNewStake, upperDataState)) return null;

    const minStake = getBetMinStake(config, selections.current, optimisticMainStateWithNewStake, upperDataState);
    const maxStake = getBetMaxStake(config, optimisticMainStateWithNewStake);

    if (_.isNil(minStake) || _.isNil(maxStake)) return null;

    return ["Min Bet Amount {1} - Max Bet Amount {2}", `${minStake}`, `${maxStake}`];
  }, [config, selections, mainState, totalStake, upperDataState]);

  return (
    <div className="B-popup-content B-calculator-popup">
      <h3 className="B-popup-title">
        {t('System Calculator')}
        <i
          onClick={() => defer.resolve(false)}
          className="global-icon icon-Close"
        />
      </h3>

      <div className="B-popup-main-content">
        <NumericInput
          value={totalStake}
          onChange={(val) => setTotalStake(val)}
          className="B-input-wrapper"
          placeholder="Total stake"
          maxLength={12}
        />

        <div className={`B-variants-table ${currentVariants.length > 9 ? 'B-variants-table-scroll' : ''}`}>
          <div className="B-variants-table-header">
            <div>
              {t('Selection')}
            </div>
            <div>
              {t('Amount')}
            </div>
            <div>
              {t('Odd')}
            </div>
            <div>
              {t('Payment')}
            </div>
          </div>

          <div className="B-variants-list" onScroll={onScroll}>
            {currentVariants.map((item) => (
              <CalculatorVariant
                key={item.name}
                variant={item}
                unitStake={unitStake}
                price={bankersTotalPrice * item.totalPrice}
              />
            ))}
          </div>
        </div>

        <div className="B-bet-info">
          <div className="B-bet-info-item">
            <span>{t('System Min Count')}:</span>
            <span>{systemCombinationCount}</span>
          </div>
          <div className="B-bet-info-item">
            <span>{t('System Variants Count')}:</span>
            <span>{optionVariantsCount}</span>
          </div>
          <div className="B-bet-info-item">
            <span>{t('Maximum Possible Win Amount')}:</span>
            <span><Money amount={possibleWin} /></span>
          </div>
        </div>

        <div className="B-profit-info">
          <div className="B-profit-info-item">
            <span>{t('Returns')}</span>
            <span><Money amount={possibleWin} /></span>
          </div>
          <div className="B-profit-info-item">
            <span>{t('Profit')}</span>
            <span><Money amount={possibleWin - +totalStake} /></span>
          </div>
        </div>
      </div>

      <NotifyMessageItem
        type={NotifyTypeEnum.Info}
        message={stakeErrorMessage}
      />

      <button
        disabled={!!stakeErrorMessage}
        className="B-popup-confirm-button"
        onClick={onSave}
      >{t('Save With Changes')}</button>
    </div>
  );
};

export default CalculatorPopup;
